import { useState, useRef, useEffect, Fragment } from 'react';
import { TourLink } from './TourLink';
import { GuidedTour, buildTimelineSteps } from './WelcomeTour';
const TIMELINE_TOUR_KEY = 'aml_tour_timeline_dismissed';
import timelineData from '@/data/timeline_events.json';
import type { TimelineFilterState } from '@/hooks/useTimelineFilterState';
import type { ActId } from '@/types';
import styles from './Timeline.module.css';

interface TimelineEvent {
  date: string;
  time?: string;
  label: string;
  type: 'milestone' | 'deadline' | 'hearing';
  phase?: string;
  instrumentId?: string;
  actId?: string;
  source?: string;
  verified?: boolean;
  verifiedFrom?: string;
}

const ACT_COLORS: Record<string, string> = {
  amld: '#1D4ED8', amlr: '#0D9488', amlar: '#7C3AED', tfr: '#C2410C',
};

const TYPE_CONFIG = {
  milestone: { label: 'Milestone', bg: '#EFF6FF', color: '#1D4ED8' },
  deadline:  { label: 'Deadline',  bg: '#FEF3C7', color: '#B45309' },
  hearing:   { label: 'Hearing',   bg: '#F3E8FF', color: '#6D28D9' },
};

// Sub-type label for each phase, shown after the type tag (e.g. "Deadline — Consultation closed").
const PHASE_LABEL: Record<string, string> = {
  'signed':              'Signed',
  'entry-into-force':    'Entry into force',
  'application':         'Application date',
  'transposition':       'Transposition deadline',
  'operational':         'Operational',
  'transfer':            'Transfer',
  'final-report':        'Final report',
  'adopted':             'Adopted',
  'consultation-open':   'Consultation open',
  'consultation-closed': 'Consultation closed',
  'final-draft':         'Final draft',
  'statutory':           'Statutory deadline',
  'submission':          'Submission to EC',
  // 'hearing' intentionally omitted: the type tag already reads "Hearing".
};

// Composes the badge text: "Milestone — Entry into force", "Deadline — Final draft", or just "Hearing".
function typeTagText(type: string, phase?: string): string {
  const base = TYPE_CONFIG[type as keyof typeof TYPE_CONFIG]?.label || type;
  const sub = phase ? PHASE_LABEL[phase] : undefined;
  return sub ? `${base} — ${sub}` : base;
}

const TODAY = new Date().toISOString().slice(0, 10);
const CURRENT_YEAR = new Date().getFullYear();
const TODAY_LABEL = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function getQuarter(d: string) { return `Q${Math.ceil(parseInt(d.slice(5,7))/3)}`; }
function getYear(d: string) { return parseInt(d.slice(0,4)); }
function formatDate(iso: string) {
  return new Date(iso+'T00:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
}

function groupEvents(events: TimelineEvent[]) {
  const byYear: Record<number, Record<string, TimelineEvent[]>> = {};
  for (const ev of events) {
    const y = getYear(ev.date), q = getQuarter(ev.date);
    if (!byYear[y]) byYear[y] = {};
    if (!byYear[y][q]) byYear[y][q] = [];
    byYear[y][q].push(ev);
  }
  return byYear;
}

// Visual-only aggregation: multiple 'statutory' events that share the same date are
// collapsed into one expandable card (the raw events stay in the data — filters,
// counts and R9 coherence are unaffected). Threshold >= 2; a lone statutory event
// renders as a normal card. Members are grouped by deliverable kind for the expand.
type AggEvent = TimelineEvent & { _agg?: TimelineEvent[] };
function collapseStatutory(list: TimelineEvent[]): AggEvent[] {
  const byDate: Record<string, TimelineEvent[]> = {};
  for (const ev of list) {
    if (ev.phase === 'statutory') (byDate[ev.date] = byDate[ev.date] || []).push(ev);
  }
  const out: AggEvent[] = [];
  const consumed = new Set<TimelineEvent>();
  for (const ev of list) {
    if (ev.phase === 'statutory') {
      const grp = byDate[ev.date];
      if (grp.length >= 2) {
        if (consumed.has(ev)) continue;
        grp.forEach(g => consumed.add(g));
        out.push({ ...grp[0], label: '', _agg: grp });
        continue;
      }
    }
    out.push(ev);
  }
  return out;
}
function aggKind(ev: TimelineEvent): string {
  if (ev.label.includes('issue guidelines')) return 'Guidelines';
  if (ev.label.includes('draft RTS')) return 'RTS';
  if (ev.label.includes('draft ITS')) return 'ITS';
  return 'Other';
}
function aggShortLabel(ev: TimelineEvent): string {
  // "RTS AMLD Art. 40(2) FS — statutory deadline: ..." -> "AMLD Art. 40(2) FS"
  return ev.label.split('—')[0].replace(/^(RTS|ITS|GL)\s+/, '').trim();
}

export function Timeline({ tlFilters, visibleActs }: { tlFilters: TimelineFilterState; visibleActs: Set<ActId> }) {
  const [expandedSource, setExpandedSource] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);

  // On mount (and when the view/filters change), scroll so that TODAY sits near the
  // top with one event still visible above it — past on top, upcoming below.
  useEffect(() => {
    const cont = containerRef.current, marker = todayRef.current;
    if (!cont || !marker) return;
    // Position the marker ~1 event-height below the top so the immediately-preceding
    // event stays visible above the TODAY line.
    const prev = marker.previousElementSibling as HTMLElement | null;
    const lead = prev ? prev.offsetHeight + 16 : 90;
    cont.scrollTop = Math.max(0, marker.offsetTop - cont.offsetTop - lead);
  }, [tlFilters.period, tlFilters.types, tlFilters.verification, visibleActs]);
  // "Today" button in the bar → scroll the marker into view
  useEffect(() => {
    const onToday = () => todayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.addEventListener('aml:timeline-today', onToday);
    return () => window.removeEventListener('aml:timeline-today', onToday);
  }, []);

  // Apply filters
  const allEvents = (timelineData as {events: TimelineEvent[]}).events;
  const filteredEvents = allEvents.filter(ev => {
    // Focus on (act filter)
    if (ev.actId && !visibleActs.has(ev.actId as ActId)) return false;

    // Period filter
    if (tlFilters.period === 'past' && ev.date >= TODAY) return false;
    if (tlFilters.period === 'future' && ev.date < TODAY) return false;
    if (tlFilters.period === 'thisYear' && parseInt(ev.date.slice(0,4)) !== CURRENT_YEAR) return false;

    // Type filter (empty = all)
    if (tlFilters.types.length > 0 && !tlFilters.types.includes(ev.type)) return false;

    // Verification filter (empty = all)
    if (tlFilters.verification.length > 0) {
      const evVerif = ev.verified ? 'confirmed' : 'unconfirmed';
      if (!tlFilters.verification.includes(evVerif as 'confirmed' | 'unconfirmed')) return false;
    }

    return true;
  });

  const events = filteredEvents.slice().sort((a,b)=>a.date.localeCompare(b.date));
  const totalEvents = allEvents.length;
  const grouped = groupEvents(events);
  const years = Object.keys(grouped).map(Number).sort((a,b)=>a-b);

  // TODAY marker: show only in 'all' / 'thisYear' views (redundant in past/future).
  // Anchor it to the first event whose date is today or later.
  // View tour: first time the Timeline is opened (own dismissal key), re-openable from the Tour link.
  const [showTour, setShowTour] = useState(false); // secondary tours never auto-open — only via the Tour link
  const showTodayMarker = tlFilters.period === 'all' || tlFilters.period === 'thisYear';
  const firstFutureEvent = showTodayMarker ? events.find(ev => ev.date >= TODAY) : undefined;

  return (
    <div className={styles.container} ref={containerRef}>
      <GuidedTour open={showTour} onClose={() => setShowTour(false)} steps={buildTimelineSteps()} storageKey={TIMELINE_TOUR_KEY} />
      <div className={styles.layout}>

        <div className={styles.main}>
          <h2 className={styles.heading} data-tour="tl-heading">AML/CFT Framework Timeline<span className={styles.todayStamp}>Today: <b>{TODAY_LABEL}</b></span><TourLink onClick={() => setShowTour(true)} /></h2>

        <div className={styles.timeline}>
          {years.map(year => {
            const quarters = Object.keys(grouped[year]).sort();
            const isPastYear = year < CURRENT_YEAR;

            return (
              <div key={year} className={styles.yearBlock}>
                <div className={`${styles.yearMarker} ${isPastYear ? styles.pastYear : ''}`}>
                  <span className={styles.yearLabel}>{year}</span>
                </div>

                <div className={styles.quartersBlock}>
                  {quarters.map(q => (
                    <div key={q} className={styles.quarterBlock}>
                      <div className={styles.quarterLabel}>{q}</div>
                      <div className={styles.events}>
                        {collapseStatutory(grouped[year][q]).map((ev, i) => {
                          const isPast = ev.date < TODAY;
                          const accentColor = ev.actId ? ACT_COLORS[ev.actId] : '#64748B';
                          const typeConfig = TYPE_CONFIG[ev.type as keyof typeof TYPE_CONFIG] || TYPE_CONFIG.milestone;
                          const sid = `${year}-${q}-${i}`;
                          const showMarkerHere = ev === firstFutureEvent;

                          // Aggregated statutory card (multiple same-date statutory deadlines)
                          if (ev._agg) {
                            const members = ev._agg;
                            const kinds: Record<string, TimelineEvent[]> = {};
                            for (const mEv of members) (kinds[aggKind(mEv)] = kinds[aggKind(mEv)] || []).push(mEv);
                            return (
                              <Fragment key={i}>
                                {showMarkerHere && (
                                  <div className={styles.todayMarker} ref={todayRef} data-tour="tl-today">
                                    <div className={styles.todayDot} />
                                    <div className={styles.todayLabel}>TODAY <span className={styles.tdate}>{TODAY_LABEL}</span></div>
                                    <div className={styles.todayLine} />
                                  </div>
                                )}
                                <div className={`${styles.event} ${isPast ? styles.pastEvent : styles.futureEvent}`}>
                                  <div className={styles.dotCol}>
                                    <div className={styles.dot} style={{ background: isPast ? '#CBD5E1' : '#475569' }} />
                                    <div className={styles.connector} />
                                  </div>
                                  <div className={styles.content}>
                                    <div className={styles.dateRow}>
                                      <span className={styles.date}>{formatDate(ev.date)}</span>
                                      <span className={styles.typeTag} style={{ background: TYPE_CONFIG.deadline.bg, color: TYPE_CONFIG.deadline.color }}>Deadline — Issue Statutory Deadline</span>
                                      <span className={styles.aggCount}>{members.length} mandates</span>
                                    </div>
                                    <div className={styles.label}>
                                      <b>Issue Statutory Deadline for AMLA deliverables</b> — {members.length} mandates due by {formatDate(ev.date)} under the AMLR/AMLD/AMLAR base articles
                                    </div>
                                    <details className={styles.aggExpand} data-tour="tl-agg">
                                      <summary>Show the {members.length} mandates ▾</summary>
                                      <div className={styles.aggList}>
                                        {Object.keys(kinds).sort().map(k => (
                                          <div key={k}>
                                            <div className={styles.aggKind}>{k} · {kinds[k].length}</div>
                                            {kinds[k].map((mEv, j) => (
                                              <a key={j} className={styles.aggItem} href={mEv.source} target="_blank" rel="noopener noreferrer">{aggShortLabel(mEv)}</a>
                                            ))}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  </div>
                                </div>
                              </Fragment>
                            );
                          }

                          return (
                            <Fragment key={i}>
                              {showMarkerHere && (
                                <div className={styles.todayMarker} ref={todayRef} data-tour="tl-today">
                                  <div className={styles.todayDot} />
                                  <div className={styles.todayLabel}>TODAY <span className={styles.tdate}>{TODAY_LABEL}</span></div>
                                  <div className={styles.todayLine} />
                                </div>
                              )}
                            <div className={`${styles.event} ${isPast ? styles.pastEvent : styles.futureEvent}`}>
                              <div className={styles.dotCol}>
                                <div className={styles.dot} style={{background: isPast ? '#CBD5E1' : accentColor}} />
                                <div className={styles.connector} />
                              </div>
                              <div className={styles.content} data-tour="tl-event">
                                <div className={styles.dateRow}>
                                  <span className={styles.date}>{formatDate(ev.date)}{ev.time ? ` · ${ev.time}` : ''}</span>
                                  {ev.actId && <span className={styles.actTag} style={{color:accentColor}}>{ev.actId.toUpperCase()}</span>}
                                  <span className={styles.typeTag} style={{background:typeConfig.bg,color:typeConfig.color}}>{typeTagText(ev.type, ev.phase)}</span>
                                </div>
                                <div className={styles.label}>
                                  {ev.source
                                    ? <a href={ev.source} target="_blank" rel="noopener noreferrer">{ev.label}</a>
                                    : ev.label}
                                </div>
                                <div className={styles.sourceBadgeRow} data-tour="tl-verified">
                                  <button
                                    className={ev.verified ? styles.verifiedSource : styles.unverifiedSource}
                                    onClick={() => setExpandedSource(expandedSource === sid ? null : sid)}
                                  >
                                    {ev.verified ? '✓ Confirmed' : '⚠ Not yet confirmed'} {expandedSource === sid ? '▲' : '▼'}
                                  </button>
                                  {expandedSource === sid && (
                                    <div className={styles.sourceDetail}>
                                      <span>{ev.verifiedFrom || 'Source not specified'}</span>
                                      {ev.source && <a href={ev.source} target="_blank" rel="noopener noreferrer">Official source ↗</a>}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            </Fragment>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          Source: EUR-Lex official texts + AMLA Annex XI SPD 2026-2028 ·
          Generated: {new Date((timelineData as {generatedAt:string}).generatedAt).toLocaleDateString('en-GB')}
        </div>
        </div>

        <aside className={styles.legendBox} data-tour="tl-legend">
          <div className={styles.legendBoxTitle}>Legend</div>

          <div className={styles.legendBoxSection}>
            <div className={styles.legendBoxItem}>
              <span className={styles.typeTag} style={{background: TYPE_CONFIG.milestone.bg, color: TYPE_CONFIG.milestone.color}}>Milestone</span>
              <span className={styles.legendBoxDesc}>framework event — entry into force, application date, transposition, operational, transfer, final report, adopted</span>
            </div>
            <div className={styles.legendBoxItem}>
              <span className={styles.typeTag} style={{background: TYPE_CONFIG.deadline.bg, color: TYPE_CONFIG.deadline.color}}>Deadline</span>
              <span className={styles.legendBoxDesc}>mandate phase — consultation open, consultation closed, final draft, submission to EC</span>
            </div>
            <div className={styles.legendBoxItem}>
              <span className={styles.typeTag} style={{background: TYPE_CONFIG.hearing.bg, color: TYPE_CONFIG.hearing.color}}>Hearing</span>
              <span className={styles.legendBoxDesc}>public consultation hearing hosted by AMLA</span>
            </div>
          </div>

          <div className={styles.legendDivider} />

          <div className={styles.legendBoxSection}>
            <div className={styles.legendBoxItem}>
              <span className={styles.verifiedBadge}>✓ Confirmed</span>
              <span className={styles.legendBoxDesc}>verified against official EU sources</span>
            </div>
            <div className={styles.legendBoxItem}>
              <span className={styles.unverifiedBadge}>⚠ Not yet</span>
              <span className={styles.legendBoxDesc}>awaiting public verification</span>
            </div>
          </div>

          <div className={styles.legendDivider} />

          <a href="https://www.amla.europa.eu/policy/regulatory-instruments_en" target="_blank" rel="noopener noreferrer" className={styles.legendBoxLink}>AMLA — Regulatory Instruments ↗</a>
          <div className={`${styles.legendBoxCount} ${events.length < totalEvents ? styles.filtering : ''}`}>
            {events.length === totalEvents ? `${totalEvents} events` : `${events.length} of ${totalEvents} events`}
          </div>
        </aside>

      </div>
    </div>
  );
}
